/**
 * rubber-band — shift-drag rectangle to multi-select overlay boxes.
 *
 * Companion to the insert-flow controller (which handles plain-drag in
 * empty space to CREATE a new box). The two coexist on the same overlay
 * pointerdown surface — insert-flow ignores shift-modified drags, this
 * controller only fires when shiftKey is held at pointerdown time.
 *
 * Selection rule: any overlay box that INTERSECTS the rubber-band
 * rectangle is included. (Strict containment — "fully inside" — is the
 * Finder default; intersection is the AutoCAD/Sketch default. We pick
 * intersection because the bboxes are typically small relative to the
 * page; requiring full containment forces the user to drag a much larger
 * marquee.)
 *
 * Pure helpers (`rectsIntersect`, `boxesInRect`) run in node tests; the
 * controller is browser-only.
 *
 * Migrated to TypeScript 2026-06-24 as part of rule-#24 wave
 * (ts-unit-testability-seams-260623 Phase 2).
 *
 * @module rubber-band
 */
const MIN_DRAG_PX = 3;
/**
 * Whether two axis-aligned rectangles intersect. Both rectangles use the
 * `{x, y, w, h}` shape with x/y as the top-left corner (CSS-px convention).
 */
export function rectsIntersect(a, b) {
    if (!a || !b)
        return false;
    return !(a.x + a.w < b.x || b.x + b.w < a.x ||
        a.y + a.h < b.y || b.y + b.h < a.y);
}
/**
 * Filter a list of box objects to those whose rect intersects `marquee`.
 * Each box must carry its own structId.
 *
 * @returns structIds of intersecting boxes, in input order.
 */
export function boxesInRect(boxes, marquee) {
    const out = [];
    if (!Array.isArray(boxes) || !marquee)
        return out;
    for (const b of boxes) {
        if (!b || !b.structId)
            continue;
        if (rectsIntersect({ x: b.x, y: b.y, w: b.w, h: b.h }, marquee)) {
            out.push(b.structId);
        }
    }
    return out;
}
/**
 * Controller for shift-drag rubber-band multi-select.
 *
 * @param hooks - Dependency injection for overlay container and callbacks.
 * @returns Handle with `destroy()` and `_isDraggingForTest()`.
 */
export function RubberBandController(hooks) {
    if (!hooks || !hooks.overlayContainer || typeof hooks.onCommit !== "function") {
        throw new Error("RubberBandController: overlayContainer + onCommit required");
    }
    const root = hooks.overlayContainer;
    const docOrNull = root.ownerDocument ?? (typeof document !== "undefined" ? document : null);
    if (!docOrNull)
        throw new Error("RubberBandController: no ownerDocument");
    const doc = docOrNull;
    let drag = null;
    function onPointerDown(ev) {
        if (ev.button !== 0)
            return;
        if (!ev.shiftKey)
            return; // only shift-drag triggers rubber-band
        // Only fire on empty area, not on existing bboxes/handles. Rubber-
        // band over a bbox would interfere with drag-move.
        const target = ev.target instanceof Element ? ev.target : null;
        if (target?.closest?.(".editor-overlay-bbox") ||
            target?.closest?.(".editor-resize-handle"))
            return;
        const rect = root.getBoundingClientRect();
        const origin = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
        const marqueeEl = doc.createElement("div");
        marqueeEl.className = "editor-rubberband-marquee";
        marqueeEl.setAttribute("aria-hidden", "true");
        marqueeEl.style.left = `${origin.x}px`;
        marqueeEl.style.top = `${origin.y}px`;
        marqueeEl.style.width = "0";
        marqueeEl.style.height = "0";
        root.appendChild(marqueeEl);
        drag = { origin, marqueeEl, last: { x: origin.x, y: origin.y, w: 0, h: 0 } };
        try {
            root.setPointerCapture?.(ev.pointerId);
        }
        catch (_) { /* no-op */ }
        ev.preventDefault?.();
        ev.stopPropagation?.();
    }
    function onPointerMove(ev) {
        if (!drag)
            return;
        const rect = root.getBoundingClientRect();
        const cur = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
        const r = {
            x: Math.min(drag.origin.x, cur.x),
            y: Math.min(drag.origin.y, cur.y),
            w: Math.abs(cur.x - drag.origin.x),
            h: Math.abs(cur.y - drag.origin.y),
        };
        drag.last = r;
        drag.marqueeEl.style.left = `${r.x}px`;
        drag.marqueeEl.style.top = `${r.y}px`;
        drag.marqueeEl.style.width = `${r.w}px`;
        drag.marqueeEl.style.height = `${r.h}px`;
    }
    function onPointerUp(ev) {
        if (!drag)
            return;
        const r = drag.last;
        try {
            root.releasePointerCapture?.(ev.pointerId);
        }
        catch (_) { /* no-op */ }
        cleanup();
        if (r.w < MIN_DRAG_PX && r.h < MIN_DRAG_PX)
            return;
        const boxes = (typeof hooks.getOverlayBoxes === "function")
            ? hooks.getOverlayBoxes()
            : [];
        const ids = boxesInRect(boxes, r);
        try {
            hooks.onCommit(ids);
        }
        catch (_) { /* host-side commit failure surfaced via toast; controller has no recovery */ }
    }
    function onKeyDown(ev) {
        if (!drag)
            return;
        if (ev.key === "Escape") {
            cleanup();
            ev.stopPropagation();
        }
    }
    function cleanup() {
        if (drag?.marqueeEl?.parentNode) {
            try {
                drag.marqueeEl.parentNode.removeChild(drag.marqueeEl);
            }
            catch (_) { /* DOM detach errors are harmless during cleanup */ }
        }
        drag = null;
    }
    root.addEventListener("pointerdown", onPointerDown, true);
    root.addEventListener("pointermove", onPointerMove);
    root.addEventListener("pointerup", onPointerUp);
    doc.addEventListener("keydown", onKeyDown, true);
    return {
        destroy() {
            root.removeEventListener("pointerdown", onPointerDown, true);
            root.removeEventListener("pointermove", onPointerMove);
            root.removeEventListener("pointerup", onPointerUp);
            doc.removeEventListener("keydown", onKeyDown, true);
            cleanup();
        },
        _isDraggingForTest() { return drag !== null; },
    };
}
